import type { Theme } from '@emotion/react';
import type { CSSProperties } from 'react';

import { withOpacity } from '@hrworks/design-system/functions';

import type { ButtonProps } from './Button.types';

export const getButtonColorMap = (
  theme: Theme,
): Record<
  NonNullable<Exclude<ButtonProps['variant'], 'unstyled' | 'text' | 'link'>>,
  Record<
    NonNullable<ButtonProps['color']>,
    Record<keyof Theme['eclipseTier2Color']['background']['brand']['bold'], string> & Pick<CSSProperties, 'color'>
  >
> => {
  const subtleOpacityModifier = '19%';

  return {
    filled: {
      primary: {
        color: theme.eclipseTier2Color.text.inverse,
        default: theme.eclipseTier2Color.background.brand.bold.default,
        hovered: theme.eclipseTier2Color.background.brand.bold.hovered,
        pressed: theme.eclipseTier2Color.background.brand.bold.pressed,
      },
      secondary: {
        color: theme.eclipseTier2Color.text.default,
        default: theme.eclipseTier2Color.background.neutral.subtle.default,
        hovered: theme.eclipseTier2Color.background.neutral.subtle.hovered,
        pressed: theme.eclipseTier2Color.background.neutral.subtle.selected,
      },
      warning: {
        color: theme.eclipseTier2Color.text.inverse,
        default: theme.eclipseTier2Color.background.warning.bold.default,
        hovered: theme.eclipseTier2Color.background.warning.bold.hovered,
        pressed: theme.eclipseTier2Color.background.warning.bold.pressed,
      },
      danger: {
        color: theme.eclipseTier2Color.text.inverse,
        default: theme.eclipseTier2Color.background.error.bold.default,
        hovered: theme.eclipseTier2Color.background.error.bold.hovered,
        pressed: theme.eclipseTier2Color.background.error.bold.pressed,
      },
      info: {
        color: theme.eclipseTier2Color.text.inverse,
        default: theme.eclipseTier2Color.background.info.bold.default,
        hovered: theme.eclipseTier2Color.background.info.bold.hovered,
        pressed: theme.eclipseTier2Color.background.info.bold.pressed,
      },
      success: {
        color: theme.eclipseTier2Color.text.inverse,
        default: theme.eclipseTier2Color.background.success.bold.default,
        hovered: theme.eclipseTier2Color.background.success.bold.hovered,
        pressed: theme.eclipseTier2Color.background.success.bold.pressed,
      },
    },
    subtle: {
      primary: {
        color: theme.eclipseTier2Color.text.brand.default,
        default: theme.eclipseTier2Color.background.brand.subtle.default,
        hovered: theme.eclipseTier2Color.background.brand.subtle.hovered,
        pressed: theme.eclipseTier2Color.background.brand.subtle.pressed,
      },
      secondary: {
        color: theme.eclipseTier2Color.text.default,
        default: theme.eclipseTier2Color.background.brand.subtle.default,
        hovered: theme.eclipseTier2Color.background.neutral.subtle.default,
        pressed: theme.eclipseTier2Color.background.neutral.subtle.hovered,
      },
      warning: {
        color: theme.eclipseTier2Color.background.warning.bold.default,
        default: theme.eclipseTier2Color.background.brand.subtle.default,
        hovered: withOpacity(theme.eclipseTier2Color.background.warning.bold.default, subtleOpacityModifier),
        pressed: withOpacity(theme.eclipseTier2Color.background.warning.bold.pressed, subtleOpacityModifier),
      },
      danger: {
        color: theme.eclipseTier2Color.background.error.bold.default,
        default: theme.eclipseTier2Color.background.brand.subtle.default,
        hovered: withOpacity(theme.eclipseTier2Color.background.error.bold.default, subtleOpacityModifier),
        pressed: withOpacity(theme.eclipseTier2Color.background.error.bold.pressed, subtleOpacityModifier),
      },
      info: {
        color: theme.eclipseTier2Color.background.info.bold.default,
        default: theme.eclipseTier2Color.background.brand.subtle.default,
        hovered: withOpacity(theme.eclipseTier2Color.background.info.bold.default, subtleOpacityModifier),
        pressed: withOpacity(theme.eclipseTier2Color.background.info.bold.pressed, subtleOpacityModifier),
      },
      success: {
        color: theme.eclipseTier2Color.background.success.bold.default,
        default: theme.eclipseTier2Color.background.brand.subtle.default,
        hovered: withOpacity(theme.eclipseTier2Color.background.success.bold.default, subtleOpacityModifier),
        pressed: withOpacity(theme.eclipseTier2Color.background.success.bold.pressed, subtleOpacityModifier),
      },
    },
    ghost: {
      primary: {
        color: theme.eclipseTier2Color.text.brand.default,
        default: theme.eclipseTier2Color.background.brand.subtle.default,
        hovered: theme.eclipseTier2Color.background.brand.subtle.hovered,
        pressed: theme.eclipseTier2Color.background.brand.subtle.pressed,
      },
      secondary: {
        color: theme.eclipseTier2Color.text.default,
        default: theme.eclipseTier2Color.background.brand.subtle.default,
        hovered: theme.eclipseTier2Color.background.neutral.subtle.default,
        pressed: theme.eclipseTier2Color.background.neutral.subtle.hovered,
      },
      warning: {
        color: theme.eclipseTier2Color.background.warning.bold.default,
        default: theme.eclipseTier2Color.background.brand.subtle.default,
        hovered: withOpacity(theme.eclipseTier2Color.background.warning.bold.default, subtleOpacityModifier),
        pressed: withOpacity(theme.eclipseTier2Color.background.warning.bold.pressed, subtleOpacityModifier),
      },
      danger: {
        color: theme.eclipseTier2Color.background.error.bold.default,
        default: theme.eclipseTier2Color.background.brand.subtle.default,
        hovered: withOpacity(theme.eclipseTier2Color.background.error.bold.default, subtleOpacityModifier),
        pressed: withOpacity(theme.eclipseTier2Color.background.error.bold.pressed, subtleOpacityModifier),
      },
      info: {
        color: theme.eclipseTier2Color.background.info.bold.default,
        default: theme.eclipseTier2Color.background.brand.subtle.default,
        hovered: withOpacity(theme.eclipseTier2Color.background.info.bold.default, subtleOpacityModifier),
        pressed: withOpacity(theme.eclipseTier2Color.background.info.bold.pressed, subtleOpacityModifier),
      },
      success: {
        color: theme.eclipseTier2Color.background.success.bold.default,
        default: theme.eclipseTier2Color.background.brand.subtle.default,
        hovered: withOpacity(theme.eclipseTier2Color.background.success.bold.default, subtleOpacityModifier),
        pressed: withOpacity(theme.eclipseTier2Color.background.success.bold.pressed, subtleOpacityModifier),
      },
    },
  };
};
